"use client";

import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  Panel,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { MatrixGrid } from "@/components/tools/research-workflow/matrix-grid";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  allTags,
  emptyRow,
  filterRows,
  type Matrix,
  matrixFromCsv,
  matrixToCsv,
  matrixToHtml,
  matrixToMarkdown,
  matrixToTsv,
  mergeMatrices,
  parseTags,
  readSavedMatrix,
  type SortKey,
  sortRows,
} from "@/lib/tool-kit/research-workflow/lit-matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const LITERATURE_REVIEW_MATRIX_STRINGS = {
  savedNote:
    "Saved in this browser as you type — nothing is uploaded. Download a CSV to keep a copy or to move the matrix to another device.",
  blockedNote:
    "This browser is not letting the page save, so the matrix will be lost when you leave. Download a CSV to keep it.",
  addSource: "Add a source",
  importCsv: "Import CSV",
  importLabel: "Import a CSV file",
  downloadCsv: "Download CSV",
  downloadMarkdown: "Download Markdown",
  copyTable: "Copy table for Word or Google Docs",
  copied: "Copied!",
  clearAll: "Clear matrix",
  clearConfirm:
    "Delete every source and restore the default columns? This cannot be undone.",
  clearConfirmAction: "Yes, clear it",
  cancel: "Cancel",
  importError:
    "That file has no rows to import. Check that it is a CSV file with a header row.",
  importDone: "one:Imported {count} source.|other:Imported {count} sources.",
  columnsHeading: "Columns",
  columnLabel: "Column {number}",
  removeColumn: "Remove",
  removeColumnLabel: "Remove the column {column}",
  newColumnLabel: "New column",
  newColumnPlaceholder: "e.g. Theoretical framework",
  addColumn: "Add column",
  searchLabel: "Search the matrix",
  searchPlaceholder: "e.g. survey",
  tagLabel: "Theme",
  allThemes: "All themes",
  sortLabel: "Sort by",
  sortOptions: {
    manual: "Order added",
    author: "Author (A–Z)",
    yearNewest: "Year (newest first)",
    yearOldest: "Year (oldest first)",
    title: "Title (A–Z)",
  },
  count: "one:{count} source|other:{count} sources",
  showing: "Showing {shown} of {total}.",
  matrixHeading: "Your matrix",
  caption: "Literature review synthesis matrix",
  headers: {
    author: "Author",
    year: "Year",
    title: "Title",
    themes: "Themes",
  },
  actionsHeader: "Remove",
  removeSource: "Remove",
  removeSourceLabel: "Remove {source}",
  untitledSource: "source {number}",
  cellLabel: "{column} — {source}",
  authorPlaceholder: "e.g. Lee & Park",
  yearPlaceholder: "e.g. 2021",
  titlePlaceholder: "Title",
  themesPlaceholder: "tags, separated by commas",
  emptyFilter: "No sources match. Clear the search or choose All themes.",
  emptyMatrix: "No sources yet — add one, or import a CSV.",
  defaultColumns: {
    aims: "Aims",
    methods: "Methods",
    sample: "Sample",
    findings: "Findings",
    limitations: "Limitations",
  },
} as const;

const STORAGE_KEY = "citationeasy:literature-review-matrix:v1";
const MAX_COLUMNS = 12;
const SORT_KEYS: readonly SortKey[] = [
  "manual",
  "author",
  "yearNewest",
  "yearOldest",
  "title",
];

type Storage = "pending" | "saved" | "blocked";

function initialMatrix(
  columns: (typeof LITERATURE_REVIEW_MATRIX_STRINGS)["defaultColumns"]
): Matrix {
  return {
    columns: Object.entries(columns).map(([id, label]) => ({ id, label })),
    rows: [emptyRow("r1")],
  };
}

export default function LiteratureReviewMatrixWidget() {
  const strings = useToolUi(LITERATURE_REVIEW_MATRIX_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [matrix, setMatrix] = useState<Matrix>(() =>
    initialMatrix(strings.defaultColumns)
  );
  const [hydrated, setHydrated] = useState(false);
  const [storage, setStorage] = useState<Storage>("pending");
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState<SortKey>("manual");
  const [newColumn, setNewColumn] = useState("");
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importFailed, setImportFailed] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const counter = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  /** A fresh id — only ever called from event handlers, never during render. */
  const makeId = (kind: "row" | "column") => {
    counter.current += 1;
    return `${kind === "row" ? "r" : "c"}${Date.now().toString(36)}${counter.current}`;
  };

  useEffect(() => {
    try {
      const saved = readSavedMatrix(window.localStorage.getItem(STORAGE_KEY));
      if (saved && (saved.rows.length > 0 || saved.columns.length > 0)) {
        setMatrix(saved);
      }
      setStorage("saved");
    } catch {
      setStorage("blocked");
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(matrix));
    } catch {
      setStorage("blocked");
    }
  }, [matrix, hydrated]);

  const updateRow = (
    id: string,
    patch: (row: Matrix["rows"][number]) => void
  ) =>
    setMatrix((current) => ({
      ...current,
      rows: current.rows.map((row) => {
        if (row.id !== id) {
          return row;
        }
        const next = { ...row, cells: { ...row.cells } };
        patch(next);
        return next;
      }),
    }));

  const addRow = () =>
    setMatrix((current) => ({
      ...current,
      rows: [...current.rows, emptyRow(makeId("row"))],
    }));

  const addColumn = () => {
    const label = newColumn.trim();
    if (!label) {
      return;
    }
    setMatrix((current) => ({
      ...current,
      columns: [...current.columns, { id: makeId("column"), label }],
    }));
    setNewColumn("");
  };

  const clearAll = () => {
    setMatrix(initialMatrix(strings.defaultColumns));
    setQuery("");
    setTag("");
    setConfirmingClear(false);
  };

  const importFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const incoming = matrixFromCsv(await file.text(), strings.headers, makeId);
    if (!incoming || incoming.rows.length === 0) {
      setImportFailed(true);
      setImportMessage(strings.importError);
      return;
    }
    setMatrix((current) => mergeMatrices(current, incoming));
    setImportFailed(false);
    setImportMessage(
      tPlural(strings.importDone, locale, incoming.rows.length, {
        count: fmt(incoming.rows.length),
      })
    );
  };

  const positions = useMemo(
    () => new Map(matrix.rows.map((row, index) => [row.id, fmt(index + 1)])),
    [matrix.rows, fmt]
  );
  const tags = useMemo(
    () => allTags(matrix.rows, locale),
    [matrix.rows, locale]
  );
  const visible = useMemo(
    () => sortRows(filterRows(matrix.rows, query, tag), sort, locale),
    [matrix.rows, query, tag, sort, locale]
  );
  const total = matrix.rows.length;

  return (
    <div className="space-y-6">
      <Notice>
        {storage === "blocked" ? strings.blockedNote : strings.savedNote}
      </Notice>

      <div className="flex flex-wrap gap-3 print:hidden">
        <button className={BUTTON_PRIMARY} onClick={addRow} type="button">
          {strings.addSource}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => fileInput.current?.click()}
          type="button"
        >
          {strings.importCsv}
        </button>
        <label className="sr-only" htmlFor="lrm-import">
          {strings.importLabel}
        </label>
        <input
          accept=".csv,text/csv,text/plain"
          className="hidden"
          id="lrm-import"
          onChange={importFile}
          ref={fileInput}
          tabIndex={-1}
          type="file"
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              "literature-review-matrix.csv",
              matrixToCsv(matrix, strings.headers, true),
              "text/csv;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.downloadCsv}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              "literature-review-matrix.md",
              matrixToMarkdown(matrix, strings.headers),
              "text/markdown;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.downloadMarkdown}
        </button>
        <CopyButton
          copiedLabel={strings.copied}
          html={matrixToHtml(matrix, strings.headers)}
          label={strings.copyTable}
          text={matrixToTsv(matrix, strings.headers)}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setConfirmingClear(true)}
          type="button"
        >
          {strings.clearAll}
        </button>
      </div>
      {confirmingClear ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 print:hidden">
          <p className="text-foreground text-sm">{strings.clearConfirm}</p>
          <button className={BUTTON_SECONDARY} onClick={clearAll} type="button">
            {strings.clearConfirmAction}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setConfirmingClear(false)}
            type="button"
          >
            {strings.cancel}
          </button>
        </div>
      ) : null}
      {importMessage && importFailed ? (
        <ErrorNotice>{importMessage}</ErrorNotice>
      ) : null}
      {importMessage && !importFailed ? (
        <output className={`block ${HINT}`}>{importMessage}</output>
      ) : null}

      <Panel title={strings.columnsHeading}>
        <div className="grid gap-3 sm:grid-cols-2">
          {matrix.columns.map((column, index) => (
            <div className="flex items-end gap-2" key={column.id}>
              <div className="flex-1">
                <TextField
                  id={`lrm-column-${column.id}`}
                  label={t(strings.columnLabel, { number: fmt(index + 1) })}
                  onChange={(value) =>
                    setMatrix((current) => ({
                      ...current,
                      columns: current.columns.map((c) =>
                        c.id === column.id ? { ...c, label: value } : c
                      ),
                    }))
                  }
                  value={column.label}
                />
              </div>
              <button
                aria-label={t(strings.removeColumnLabel, {
                  column: column.label,
                })}
                className={BUTTON_SMALL}
                onClick={() =>
                  setMatrix((current) => ({
                    ...current,
                    columns: current.columns.filter((c) => c.id !== column.id),
                  }))
                }
                type="button"
              >
                {strings.removeColumn}
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <TextField
              id="lrm-new-column"
              label={strings.newColumnLabel}
              onChange={setNewColumn}
              placeholder={strings.newColumnPlaceholder}
              value={newColumn}
            />
          </div>
          <button
            className={BUTTON_SECONDARY}
            disabled={!newColumn.trim() || matrix.columns.length >= MAX_COLUMNS}
            onClick={addColumn}
            type="button"
          >
            {strings.addColumn}
          </button>
        </div>
      </Panel>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.matrixHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-3 print:hidden">
          <TextField
            id="lrm-search"
            label={strings.searchLabel}
            onChange={setQuery}
            placeholder={strings.searchPlaceholder}
            type="search"
            value={query}
          />
          <SelectField
            id="lrm-tag"
            label={strings.tagLabel}
            onChange={setTag}
            options={[
              { value: "", label: strings.allThemes },
              ...tags.map((value) => ({ value, label: value })),
            ]}
            value={tag}
          />
          <SelectField<SortKey>
            id="lrm-sort"
            label={strings.sortLabel}
            onChange={setSort}
            options={SORT_KEYS.map((value) => ({
              value,
              label: strings.sortOptions[value],
            }))}
            value={sort}
          />
        </div>
        <p aria-live="polite" className={HINT}>
          {tPlural(strings.count, locale, total, { count: fmt(total) })}
          {visible.length === total
            ? null
            : ` · ${t(strings.showing, {
                shown: fmt(visible.length),
                total: fmt(total),
              })}`}
        </p>
        {visible.length > 0 ? (
          <MatrixGrid
            columns={matrix.columns}
            labels={strings}
            onCell={(id, column, value) =>
              updateRow(id, (row) => {
                row.cells[column] = value;
              })
            }
            onField={(id, field, value) =>
              updateRow(id, (row) => {
                row[field] = value;
              })
            }
            onRemove={(id) =>
              setMatrix((current) => ({
                ...current,
                rows: current.rows.filter((row) => row.id !== id),
              }))
            }
            onTags={(id, text) =>
              updateRow(id, (row) => {
                row.tags = parseTags(text);
              })
            }
            positions={positions}
            rows={visible}
          />
        ) : (
          <Notice>
            {total === 0 ? strings.emptyMatrix : strings.emptyFilter}
          </Notice>
        )}
      </section>
    </div>
  );
}
